import React, { FC, useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { setPath, setUrlParam } from '../../../../utils/url'
import { URL } from '../../../constants/url'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import CalendarView from './CalendarView'
import { format, nowDate, startOfLastWeek, startOfWeek, weekList } from '../../../../utils/date'
import { useTranslation } from 'react-i18next'
import { uniqueId } from 'lodash'

type LangValue = 'ja' | 'en'

interface Props {
  reports: {
    date: string
    total_evaluation: string
    weekly_reports: {
      date: string
      is_read: boolean
    }[]
    is_read: boolean
  }[]
  id: number
  name: string
  year: string
}

const MemberList: FC<Props> = (props) => {
  const {
    reports,
    id,
    name,
    year,
  } = props

  const { language } = useParams<{ language: LangValue }>()
  const history = useHistory()
  const { t } = useTranslation()

  const [displayMonth, setDisplayMonth] = useState<number>(0)

  const displayRange = useMemo(() => {
    if (!displayMonth) return []
    return weekList(`${year}-${displayMonth}-01`, "YYYY-MM-DD")
  }, [displayMonth, year])

  const weeklyReports = useMemo(() => {
    if (!displayMonth) return []
    return reports.find((report) => Number(format(report.date, "M")) === displayMonth)?.weekly_reports
  }, [displayMonth, reports])

  const nextMonth = () => {
    if (
      (Number(format(`${year}-${displayMonth}-01`, "YYYYMM")) >= Number(format(startOfWeek(nowDate()), "YYYYMM")))
      || (displayMonth === 12)
    ) return
    setDisplayMonth(displayMonth + 1)
  }

  const prevMonth = () => {
    if (displayMonth === 1) return
    setDisplayMonth(displayMonth - 1)
  }

  const clickReport = (member_id: number, range: string) => {
    history.push(`${setPath(setUrlParam(
      URL.REPORT_DETAIL_WEEKLY.path,
      [
        { key: 'target', value: "member" },
        { key: 'id', value: member_id.toString() },
      ],
    ), language)}?date=${range}`)
  }

  useEffect(() => {
    if (!reports) return
    setDisplayMonth(Number(format(startOfLastWeek(nowDate()), "M")))
  }, [reports])

  return (
    <div className="content-block">
      <div className="block-header">
        <div className="title no_trans">
          {name}
        </div>
        <div className="weekly_list">
          <div
            className="change_month"
            onClick={() => prevMonth()}
            role="none"
          >
            <FontAwesomeIcon icon={['fas', 'angle-left']} />
          </div>
          <div className="graph_area">
            <div className="line" />
            {
              displayRange?.map((range) => (
                <div className="block" key={uniqueId()}>
                  {
                    weeklyReports?.find((report) => report.date === range)?.date ? (
                      <>
                        <div className="check">
                          <FontAwesomeIcon icon={['fas', 'check']} />
                        </div>
                        <div className="text_area">
                          {range}
                          {
                            weeklyReports?.find((report) => report.date === range)?.is_read ? (
                              <>
                              </>
                            ) : (
                              <div className="new">
                                New
                              </div>
                            )
                          }
                        </div>
                        <button
                          data-gtm-click="weekly_report - header - button"
                          type="button"
                          className="view_report"
                          onClick={() => clickReport(id, range)}
                        >
                          {t('report.list.box.view_report')}
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="no_check" />
                        <div className="text_area">
                          {range}
                        </div>
                      </>
                    )
                  }
                </div>
              ))
            }
          </div>
          <div
            className="change_month"
            onClick={() => nextMonth()}
            role="none"
          >
            <FontAwesomeIcon icon={['fas', 'angle-right']} />
          </div>
        </div>
      </div>
      <div className="block-detail">
        <div className="card_area">
          {
            Array(12).fill(null).map((val, key) => (
              <CalendarView
                key={uniqueId()}
                target="member"
                id={id}
                year={year}
                month={(key + 1).toString()}
                list={reports.find((value) => format(value.date, "M") === (key + 1).toString())}
              />
            ))
          }
        </div>
      </div>
    </div>
  )
}

export default MemberList
